import HttpClient from '../httpClient';
import { toPublicErrorMessage } from '../../utils/publicError';
import type {
  StripeRegion,
  EnterpriseSetupIntentResponse,
  EnterpriseSetupIntentRequest,
} from '../../components/billing/types';
import { Invoice } from '../../hooks/billing/useInvoices';

const PAYMENT_ACTIONS_PATH = '/enterprisePaymentActions';

type PaymentActionsPayload = {
  type: 'list' | 'delete' | 'default' | 'update' | 'attach';
  customerId?: string;
  orgId?: string;
  paymentMethodId?: string;
  email?: string;
  name?: string;
  cardDetails?: Record<string, unknown>;
  setAsDefault?: boolean;
  source?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
};

type PaymentActionResponse = {
  status: boolean;
  data?: unknown;
  message?: string;
};

function checkPaymentActionResponse(
  res: PaymentActionResponse,
): asserts res is { status: true; data: unknown } {
  if (!res.status) {
    throw new Error(res.message || 'Payment action failed');
  }
}

class BillingController extends HttpClient {
  private async postPaymentAction(
    payload: PaymentActionsPayload,
    region: string,
    token: string,
  ): Promise<PaymentActionResponse> {
    const { data } = await this.client.post(PAYMENT_ACTIONS_PATH, payload, {
      headers: {
        Authorization: token,
        region,
        'Content-Type': 'application/json',
      },
    });
    return data as PaymentActionResponse;
  }

  getPaymentMethods = async (
    customerId: string,
    orgId: string,
    region: string,
    token: string,
  ) => {
    const res = await this.postPaymentAction(
      { type: 'list', customerId, orgId },
      region,
      token,
    );
    checkPaymentActionResponse(res);
    return res.data as {
      paymentMethods?: unknown[];
      defaultPaymentId?: string;
    };
  };

  addPaymentMethod = async (
    payload: {
      customerId?: string;
      orgId?: string;
      paymentMethodId: string;
      setAsDefault?: boolean;
      email?: string;
      name?: string;
      address?: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        postal_code: string;
        country: string;
      };
    },
    region: string,
    token: string,
  ) => {
    const res = await this.postPaymentAction(
      {
        type: 'attach',
        customerId: payload.customerId,
        orgId: payload.orgId,
        paymentMethodId: payload.paymentMethodId,
        setAsDefault: payload.setAsDefault,
        email: payload.email,
        name: payload.name,
        address: payload.address,
        source: 'enterprise',
      },
      region,
      token,
    );
    checkPaymentActionResponse(res);
    return res.data as { attached?: boolean; customerId?: string };
  };

  updatePaymentMethod = async (
    paymentMethodId: string,
    payload: {
      customerId?: string;
      orgId?: string;
      setAsDefault?: boolean;
      name?: string;
      email?: string;
      address?: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        postal_code: string;
        country: string;
      };
    },
    region: string,
    token: string,
  ) => {
    const hasBillingUpdate = payload.address || payload.name || payload.email;
    if (hasBillingUpdate) {
      const billingDetails: Record<string, unknown> = {};
      if (payload.address) billingDetails.address = payload.address;
      if (payload.name) billingDetails.name = payload.name;
      if (payload.email) billingDetails.email = payload.email;
      const updateRes = await this.postPaymentAction(
        {
          type: 'update',
          paymentMethodId,
          cardDetails: {
            billing_details: billingDetails,
          },
          orgId: payload.orgId,
        },
        region,
        token,
      );
      checkPaymentActionResponse(updateRes);
    }
    if (payload.setAsDefault && payload.customerId) {
      const defaultRes = await this.postPaymentAction(
        {
          type: 'default',
          customerId: payload.customerId,
          paymentMethodId,
          orgId: payload.orgId,
        },
        region,
        token,
      );
      checkPaymentActionResponse(defaultRes);
    }
    return {};
  };

  deletePaymentMethod = async (
    paymentMethodId: string,
    orgId: string,
    region: string,
    token: string,
  ) => {
    const res = await this.postPaymentAction(
      { type: 'delete', paymentMethodId, orgId },
      region,
      token,
    );
    checkPaymentActionResponse(res);
    return res.data;
  };

  // setDefaultPaymentMethod = async (
  //   paymentMethodId: string,
  //   customerId: string,
  //   region: string,
  //   token: string,
  // ) => {
  //   const res = await this.postPaymentAction(
  //     { type: 'default', customerId, paymentMethodId },
  //     region,
  //     token,
  //   );
  //   checkPaymentActionResponse(res);
  //   return res.data;
  // };

  updateBillingContact = async (
    payload: {
      orgId?: string;
      customerId?: string;
      address: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        postal_code: string;
        country: string;
      };
    },
    region: string,
    token: string,
  ) => {
    const response = await this.client.put('/billing-contact', payload, {
      headers: {
        Authorization: token,
        region,
      },
    });
    return response.data;
  };

  getBillingDetails = async (
    customerId: string,
    region: string,
    token: string,
  ) => {
    const response = await this.client.get(`/billings/${customerId}`, {
      headers: {
        Authorization: token,
        region,
      },
    });
    return response.data;
  };

  getCurrentMonthBill = async (
    orgId: string,
    region: string,
    token: string,
  ) => {
    const response = await this.client.post(
      '/current-month-bill',
      { orgId },
      {
        headers: {
          Authorization: token,
          region,
          'Content-Type': 'application/json',
        },
      },
    );
    return response.data;
  };

  getEnterpriseInvoices = async (orgId: string, token: string) => {
    const res = await this.client.get(`/enterpriseInvoices/${orgId}`, {
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
    });

    if (res.data?.error?.message) {
      throw new Error(toPublicErrorMessage(res.data.error.message));
    }

    return (res.data?.invoices || []) as Invoice[];
  };

  getInvoices = async (
    customerId: string,
    orgId: string,
    region: string,
    token: string,
  ) => {
    const response = await this.client.post(
      '/invoices',
      { customerId, orgId },
      {
        headers: {
          Authorization: token,
          region,
          'Content-Type': 'application/json',
        },
      },
    );
    return response.data;
  };

  createSetupIntent = async (
    region: StripeRegion,
    body: EnterpriseSetupIntentRequest,
    token: string,
  ): Promise<EnterpriseSetupIntentResponse> => {
    const res = await this.client.post<EnterpriseSetupIntentResponse>(
      '/enterpriseSetupIntent',
      body,
      {
        headers: {
          Authorization: token,
          region,
          'Content-Type': 'application/json',
        },
      },
    );
    return res.data;
  };

  retryInvoicePayment = async (params: {
    orgId: string;
    invoiceId: string;
    token: string;
  }) => {
    const { orgId, invoiceId, token } = params;
    const res = await this.client.post(
      `/enterpriseInvoice/${invoiceId}/retry`,
      {
        orgId,
      },
      {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
      },
    );
    return res.data as {
      ok?: boolean;
      status?: string; // "paid" | "open" | "requires_action" | ...
      hostedInvoiceUrl?: string | null;
      message?: string;
      error?: { message?: string };
    };
  };

  /**
   * Get the per-applicant price based on region or billing country
   * @param region - Locked region from org record (optional)
   * @param country - ISO country code from billing address (optional)
   * @param token - Auth token
   */
  getEnterprisePrice = async (
    token: string,
    region?: string,
    country?: string,
  ): Promise<{
    success: boolean;
    data?: {
      price: number;
      currency: string;
      symbol: string;
      region: string;
      formatted: string;
    };
  }> => {
    const res = await this.client.post(
      '/enterprise/price',
      { region, country },
      {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
      },
    );
    return res.data;
  };
}

export default new BillingController(
  process.env.REACT_APP_API_BASE_URL_SUBSCRIPTION ?? '',
);
